"use client";

import { IconCheckOutline18 } from "nucleo-ui-outline-18";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { showToast } from "@/lib/toast";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useEffect, useRef, useState } from "react";
import { ConnectorLogo } from "./ConnectorLogo";
import type { PipedreamConnectResult } from "./usePipedreamConnect";
interface PipedreamConnectorModalProps {
  pendingConnection: PipedreamConnectResult;
  onClose: () => void;
}

/**
 * Simple modal shown after successful Pipedream OAuth.
 * Allows user to customize name/description and additional connector-specific fields before creating the connector.
 */
export function PipedreamConnectorModal({
  pendingConnection,
  onClose
}: PipedreamConnectorModalProps) {
  const {
    accountId,
    connectorType
  } = pendingConnection;
  const createConnector = useConnectorsStore(state => state.createConnector);
  const [name, setName] = useState(`${connectorType.name} Account`);
  const [description, setDescription] = useState("");
  const [data, setData] = useState<Record<string, any>>({
    accountId
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dataSchema = connectorType.dataSchema;

  // Initialize additional fields with defaults
  useEffect(() => {
    // Always reset data to match current schema (prevents stale data bug)
    const initialData: Record<string, any> = {
      accountId
    };
    if (dataSchema?.properties) {
      Object.keys(dataSchema.properties).forEach(key => {
        if (key !== "accountId") {
          initialData[key] = "";
        }
      });
    }
    setData(initialData);
    // Use stringified keys to avoid re-renders from object reference changes
  }, [JSON.stringify(Object.keys(dataSchema?.properties || {})), accountId]);

  // Protect against accidental closes from stray click events
  const isReadyRef = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      isReadyRef.current = true;
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  const handleFieldChange = (fieldName: string, value: string) => {
    setData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast.error("Please enter a connector name");
      return;
    }

    // Validate required fields
    if (dataSchema?.required) {
      for (const field of dataSchema.required) {
        if (!data[field] || data[field].toString().trim() === "") {
          const fieldSchema = dataSchema.properties?.[field];
          const fieldLabel = fieldSchema?.description || field;
          showToast.error(`${fieldLabel} is required`);
          return;
        }
      }
    }
    setIsSubmitting(true);
    try {
      await createConnector({
        type: connectorType.type,
        name: name.trim(),
        description: description.trim(),
        data
      });
      showToast.success(`${connectorType.name} connector created!`);
      onClose();
    } catch (error) {
      showToast.error(error instanceof Error ? error.message : "Failed to create connector");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleOpenChange = (open: boolean) => {
    if (!open && isReadyRef.current) {
      onClose();
    }
  };
  return <ResponsiveModal open={true} onOpenChange={handleOpenChange} title={`Add ${connectorType.name}`} contentClass="max-w-md" preventCloseOnOutsideClick={true} hideCloseButton={true}>
      <div className="space-y-6">
        {/* Success indicator */}
        <div className="flex items-center gap-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-800">
            <IconCheckOutline18 className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="font-semibold text-green-800 dark:text-green-200">Connected Successfully</p>
            <p className="text-sm text-green-600 dark:text-green-400">Account ID: {accountId.slice(0, 12)}...</p>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center gap-4">
          <ConnectorLogo type={connectorType.type} name={connectorType.name} logoUrl={connectorType.logoUrl} />
          <div>
            <h2 className="text-ds-text-primary text-lg font-bold">Finalize Connector</h2>
            <p className="body-sm">Customize your connector details</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="body-sm-medium mb-2 block">
              Name <span className="text-red-500">*</span>
            </label>
            <Input type="text" value={name} onChange={e_0 => setName(e_0.target.value)} placeholder={`My ${connectorType.name} Connector`} className="w-full rounded-lg px-4 py-2.5 transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500" required autoFocus />
          </div>

          <div>
            <label className="body-sm-medium mb-2 block">Description</label>
            <Textarea value={description} onChange={e_1 => setDescription(e_1.target.value)} placeholder="Optional description for this connector" rows={2} className="w-full rounded-lg px-4 py-2.5 transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
          </div>

          {/* Dynamic connector-specific fields from dataSchema */}
          {dataSchema?.properties && Object.entries(dataSchema.properties).map(([fieldName_0, fieldSchema_0]: [string, any]) => {
          // Skip accountId since it's already provided from OAuth
          if (fieldName_0 === "accountId") return null;
          const isRequired = dataSchema.required?.includes(fieldName_0);
          return <div key={fieldName_0}>
                  <label className="body-sm-medium mb-2 block">
                    {fieldSchema_0.description || fieldName_0}
                    {isRequired && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <Input type={fieldSchema_0.format === "uri" ? "url" : "text"} value={data[fieldName_0] || ""} onChange={e_2 => handleFieldChange(fieldName_0, e_2.target.value)} placeholder={fieldSchema_0.pattern ? `e.g., ${fieldSchema_0.pattern}` : ""} className="w-full rounded-lg px-4 py-2.5 font-mono transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500" required={isRequired} />
                </div>;
        })}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="white" onClick={() => isReadyRef.current && onClose()} type="button" disabled={isSubmitting}>
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <span className="flex items-center gap-2 text-xs">
                  <PixelLoader size="xs" />
                  Creating...
                </span> : <span className="text-xs">Create Connector</span>}
            </Button>
          </div>
        </form>
      </div>
    </ResponsiveModal>;
}